import React, { useEffect } from 'react';
import { Panels } from '../Consts';
import {
  Button, Card, CardGrid, Cell, Div,
  FixedLayout, Group, Panel, PanelHeaderButton, Placeholder,
  IconButton, PullToRefresh, Skeleton, Tabs, TabsItem,
} from '@vkontakte/vkui';
import { Icon28Profile, Icon28AddCircleOutline, Icon28ShareExternalOutline } from '@vkontakte/icons';
import { Modals } from '../../Consts';
import { UserAvatar } from '../components/UserAvatar';
import { useAppContext } from '../../AppContext';
import { ErrorLoadComponent } from '../components/ErrorLoadComponent';
import { prepareString } from '../../helpers/helpers';
import { useAppStore } from '../../AppStore';
import { getRouteCreatePayment, getRouteEventInfo, getRouteEventsArchiveList, getRouteProfile } from '../../route';
import { SearchComponent } from './components/SearchComponent';
import { PlatformPanelHeader } from '../components/PlatformPanelHeader';

const CURRENT_PANEL = Panels.EVENTS_LIST;

export const clearCache = (cache) => {
  cache.set(CURRENT_PANEL, undefined);
}

export const getEventsList = (offset, context, clearCache = false) => {
  return new Promise((resolve, reject) => {
    const cache = {
      events: [],
      count: 0,
    };

    if (!clearCache) {
      const data = context.cache.get(CURRENT_PANEL);
      if (data) {
        cache.events = data.events;
        cache.count = data.count;
        if (cache.events.length > offset) {
          return resolve({
            events: cache.events,
            count: cache.count,
          });
        }
      }
    }

    context.init().then(() => {
      context.httpClient().getEvents(offset, '').then((response) => {

        context.cache.set(CURRENT_PANEL, {
          events: [...(cache.events || []), ...(response.data.events || [])],
          count: response.data.count || 0,
        });

        return resolve({
          events: [...(cache.events || []), ...(response.data.events || [])],
          count: response.data.count || 0,
        });
      }).catch((error) => {
        return reject(error);
      });
    }).catch((error) => {
      return reject(error);
    });
  });
}

const EmptyList = () => {
  const context = useAppContext();

  return (<Placeholder
    className={'EventEmptyList__block'}
    header={<>
      {context.lang().getKey(CURRENT_PANEL, 'emptyListContentLine1')}<br />
      {context.lang().getKey(CURRENT_PANEL, 'emptyListContentLine2')}<br />
      {context.lang().getKey(CURRENT_PANEL, 'emptyListContentLine3')}
    </>}
    action={<Button
      size="l"
      onClick={() => context.router.showModal(Modals.CREATE_EVENT_MODAL)}
    >
      {context.lang().getKey(CURRENT_PANEL, 'createEventButton')}
    </Button>}
  />);
}

const ActionButtonItemComponent = ({event, share = false}) => {
  const context = useAppContext();

  if (share) {
    const onShareHandle = (e, event) => {
      e.stopPropagation();
      context.getBridge().shareInvite(event.id, event.invite, event.invite_link, context);
    }

    return (<IconButton
      aria-label={context.lang().getKey(Panels.EVENTS_LIST, 'event_share_button')}
      onClick={(e) => onShareHandle(e, event)}
    >
      <Icon28ShareExternalOutline fill={'var(--vkui--color_icon_accent_themed)'} />
    </IconButton>);
  }

  const addPaymentHandle = (e, event) => {
    e.stopPropagation();
    context.router.push(...getRouteCreatePayment(event.id, 'events_list'));
  }

  return (<IconButton
    aria-label={context.lang().getKey(Panels.EVENT_INFO, 'add_payment')}
    onClick={(e) => addPaymentHandle(e, event)}
  >
    <Icon28AddCircleOutline fill={'var(--vkui--color_icon_accent_themed)'} />
  </IconButton>);
}

const Item = ({event, share}) => {
  const context = useAppContext();

  return (<CardGrid size="l">
    <Card>
      <Cell
        before={<UserAvatar src={event.image_s} name={event.name} id={event.id} />}
        after={<ActionButtonItemComponent event={event} share={share} />}
        onClick={(e) => context.router.push(...getRouteEventInfo(event.id))}
      >
        {event.name}
      </Cell>
    </Card>
  </CardGrid>);
}

const Header = () => {
  const context = useAppContext();

  return <PlatformPanelHeader
    text={<Tabs mode={"default"} layoutFillMode={"shrinked"}>
      <TabsItem
        id="events"
        selected={true}
      >
        {context.lang().getKey(CURRENT_PANEL, 'header')}
      </TabsItem>
      <TabsItem
        id="archive"
        onClick={() => context.router.replace(...getRouteEventsArchiveList())}
        selected={false}
      >
        Архив
      </TabsItem>
    </Tabs>}
    isShowBack={false}
    before={
      <PanelHeaderButton
        onClick={() => context.router.push(...getRouteProfile())}
        aria-label={context.lang().getKey(CURRENT_PANEL, 'profile_button_label')}
      >
        <Icon28Profile />
      </PanelHeaderButton>
    }
  />
}

const SceletonPanel = () => {
  const context = useAppContext();

  return (<Panel nav={CURRENT_PANEL} className={'SceletonPanel'}>
    <PlatformPanelHeader
      text={<Skeleton width={170} />}
      isShowBack={false}
      before={
        <PanelHeaderButton
          aria-label={context.lang().getKey(CURRENT_PANEL, 'profile_button_label')}
        >
          <Skeleton width={28} height={28} borderRadius="50%" />
        </PanelHeaderButton>
      }
    />
    <Div style={{paddingBottom:0}}>
      <Skeleton width="100%" height={32}/>
    </Div>
    <Group mode="plain">
      {[1,2,3,4,5,6,7,8,9,10].map((i) => (
        <CardGrid size="l" key={i}>
          <Card>
            <Cell
              before={<Skeleton width={48} height={48} borderRadius="50%" />}
              after={<Skeleton width={24} height={24} borderRadius="50%" />}
              subtitle={<Skeleton width={i*10+10} />}
            >
            </Cell>
          </Card>
        </CardGrid>
      ))}
    </Group>
    <FixedLayout vertical="bottom" style={{textAlign: 'center'}}>
      <Div>
        <Skeleton width="100%" height={36} />
      </Div>
    </FixedLayout>
  </Panel>);
}

const EventsListPanel = ({}) => {
  const context = useAppContext();
  const appStore = useAppStore();

  const [search, setSearch] = React.useState('');
  const [events, setEvents] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const [eventsSearch, setEventsSearch] = React.useState([]);
  const [eventsSearchTotalCount, setEventsSearchTotalCount] = React.useState(0);
  const searchTimeout = React.useRef(0);
  const [isRefreshFetching, setIsRefreshFetching] = React.useState(false);
  const [isImAttachPicker, setIsImAttachPicker] = React.useState(context.appOpenParams?.vk_ref === 'im_attach_picker' || context.appOpenParams?.vk_ref === 'catalog_messenger_apps_with_action');

  const onRefresh = () => {
    if (search.length > 0) {
      return onRefreshSearch();
    }

    context.spinner(true);
    setIsRefreshFetching(true);
    getEventsList(0, context, true)
      .then(({count, events}) => {
        setTotalCount(count);
        setEvents(events);
        setError(null);
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        context.spinner(false);
        setIsLoaded(true);
        setIsRefreshFetching(false);
      });
  }

  const onRefreshSearch = () => {
    setIsRefreshFetching(true);
    setIsLoading(true);

    context.spinner(true);
    context.httpClient()
      .getEvents(0, search)
      .then((response) => {
        setEventsSearch([...response.data.events]);
        setEventsSearchTotalCount(response.data.count);
        setError(null);
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        context.spinner(false);
        setIsRefreshFetching(false);
        setIsLoading(false);
      });
  }

  const loadMoreSearch = () => {
    setIsLoading(true);

    context.httpClient()
      .getEvents(eventsSearch.length, search)
      .then((response) => {
        setEventsSearch([...eventsSearch, ...response.data.events]);
        setEventsSearchTotalCount(response.data.count);
        setError(null);
      })
      .catch((error) => {
        context.showError(error);
      })
      .finally(() => {
        context.spinner(false);
        setIsLoading(false);
      });
  }

  const loadMore = () => {
    if (search.length > 0) {
      return loadMoreSearch();
    }

    setIsLoading(true);
    getEventsList(events.length, context)
      .then(({count, events}) => {
        setTotalCount(count);
        setEvents(events);
        setError(null);
      })
      .catch((error) => {
        context.showError(error);
      })
      .finally(() => {
        context.spinner(false);
        setIsLoading(false);
      });
  }

  const onSearch = (value) => {
    if (value.length < 30) {
      setSearch(value);
    }

    if (value.length < 1) {
      return setEventsSearch([]);
    }

    const searchValue = prepareString(value).toLowerCase();

    if (events.length >= totalCount) {
      //все события загружены можем искать по списку
      return setEventsSearch(events.filter((event) => prepareString(event.name).toLowerCase().indexOf(searchValue) > -1));
    }

    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      context.httpClient()
        .getEvents(0, value)
        .then((response) => {
          setEventsSearch(response.data.events);
          setEventsSearchTotalCount(response.data.count);
        })
        .catch((error) => {
          context.showError(error);
        });
    }, 500);
  }

  useEffect(() => {
    context.spinner(true);
    getEventsList(0, context)
      .then(({count, events}) => {
        setTotalCount(count);
        setEvents(events);
        setError(null);

        if (!appStore.isShowConfirmCreateEventModal && isImAttachPicker) {
          context.confirm(
            context.lang().getKey(Panels.EVENTS_LIST, 'im_attach_picker_confirm_text'),
            (value) => {
              if (value) {
                context.router.showModal(Modals.CREATE_EVENT_MODAL);
              }
            },
            context.lang().getKey(Panels.EVENTS_LIST, 'im_attach_picker_confirm_label_ok'),
            context.lang().getKey(Panels.EVENTS_LIST, 'im_attach_picker_confirm_label_cancel'),
            true
          );
          appStore.setIsShowConfirmCreateEventModal();
        }
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        context.spinner(false);
        setIsLoaded(true);
      });
  }, []);

  if (error) {
    return (<ErrorLoadComponent
      error={error}
      onFetch={onRefresh}
    />)
  }

  if (!isLoaded) {
    return (<SceletonPanel />);
  }

  const SearchEmptyList = () => {
    return (<Placeholder
      className={'SearchEmptyList__block'}
      header={context.lang().getKey(CURRENT_PANEL, 'searchEmptyList')}
    />);
  }

  return (
    <Panel nav={CURRENT_PANEL}>
      <Header />
      {context.snackbar}
      <SearchComponent
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder={context.lang().getKey(CURRENT_PANEL, 'searchPlaceholder')}
      />
      <PullToRefresh onRefresh={onRefresh} isFetching={isRefreshFetching}>
        <Group mode="plain" style={{marginBottom: 40}}>
          {
            events.length === 0 && !search && (<EmptyList />)
          }
          {
            (search.length > 0 ? eventsSearch : events).map((event) => (<Item key={event.id} event={event} share={isImAttachPicker} />))
          }
          {
            (search.length > 0 && eventsSearch.length < 1) ? (<SearchEmptyList />) : undefined
          }
          {
            (search.length > 0 ? eventsSearch.length : events.length) < (search.length > 0 ? eventsSearchTotalCount : totalCount) ? (<Div><Button
              size="s"
              mode="tertiary"
              stretched={true}
              loading={isLoading}
              onClick={() => loadMore()}
            >{context.lang().getKey(CURRENT_PANEL, 'moreLoadButton')}</Button></Div>) : undefined
          }
        </Group>
      </PullToRefresh>
      {
        events.length > 0 && <FixedLayout vertical="bottom" style={{textAlign: 'center'}}>
          <Div>
            <Button
              size="l"
              stretched={true}
              onClick={() => context.router.showModal(Modals.CREATE_EVENT_MODAL)}
            >
              {context.lang().getKey(CURRENT_PANEL, 'createEventButton')}
            </Button>
          </Div>
        </FixedLayout>
      }
    </Panel>
  );
}

export default EventsListPanel;
